import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  InputNumber,
  Table,
  Button,
  Typography,
  Tooltip,
  Empty,
  Select,
} from 'antd';

import {
  AFS_GROUP,
  ALTERNATIVES_GROUP,
  AssetGroupAllocationPayload,
  ASSETS_OF_GROUP,
  BONDS_GROUP,
  CASH_APPROACH,
  CASH_GROUP,
  CDS_GROUP,
  createUpdateGroupAllocations,
  CRYPTO_GROUP,
  EQUITIES_GROUP,
  FX_GROUP,
  GROUP_ASSET_NAME,
  HTM_GROUP,
  MANAGEMENT_TYPES,
  OPTIONS_GROUP,
  PORTFOLIO_MANAGEMENT,
  REPO_GROUP,
  selectors,
  updateInvestmentProfile,
  updatePlayerGroupAllocations,
  WEALTH_MANAGEMENT,
} from 'state';
import { red } from '@ant-design/colors';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useModal } from 'services';
import RefreshAssetEodModal from '../../../AdminDashboard/Data/AssetsTab/RefreshAssetEodModal';
import ClientProfileModal from './ClientProfileModal';
// import { hasCDSGroup, hasRepoGroup } from '../../../state/app/selectors';

type State = {
  [assetGroup: number]: AssetGroupAllocationPayload;
};

const PlayerStrategicAssetAllocations: React.FC = () => {
  const dispatch = useDispatch();
  const {
    id,
    // asset_group_allocations,
    risk_approach,
    management_type,
    investment_profile,
  } = useSelector(selectors.activeSession);
  const {
    strategic_group_allocations,
    id: player_id,
    tactical_asset_allocations,
  } = useSelector(selectors.player);
  const [editing, setEditing] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [allocations, setAllocations] = useState<State>({});

  const [clientProfileModal, showClientProfileModal] =
    useModal(ClientProfileModal);

  const hasEquitiesGroup = useSelector(selectors.hasEquitiesGroup());
  const hasBondsGroup = useSelector(selectors.hasBondsGroup());
  const hasAlternativesGroup = useSelector(selectors.hasAlternativesGroup());
  const hasCryptoGroup = useSelector(selectors.hasCryptoGroup());
  const hasHTMGroup = useSelector(selectors.hasHTMGroup());
  const hasAFSGroup = useSelector(selectors.hasAFSGroup());
  const hasRepo = useSelector(selectors.hasRepoGroup());
  const hasCDS = useSelector(selectors.hasCDSGroup());
  const hasSimplifiedOptions = useSelector(
    selectors.hasSimplifiedOptionsGroup()
  );

  // useEffect(() => {
  //
  // }, []);

  const assetGroups = { ...GROUP_ASSET_NAME };
  // FX no longer taken into consideration in allocations table
  delete assetGroups[FX_GROUP];
  if (
    risk_approach !== CASH_APPROACH ||
    management_type === WEALTH_MANAGEMENT
  ) {
    delete assetGroups[CASH_GROUP];
  }
  if (!hasEquitiesGroup) {
    delete assetGroups[EQUITIES_GROUP];
  }
  if (!hasBondsGroup) {
    delete assetGroups[BONDS_GROUP];
  }
  if (!hasAlternativesGroup) {
    delete assetGroups[ALTERNATIVES_GROUP];
  }
  if (!hasCryptoGroup) {
    delete assetGroups[CRYPTO_GROUP];
  }
  if (!hasHTMGroup) {
    delete assetGroups[HTM_GROUP];
  }
  if (!hasAFSGroup) {
    delete assetGroups[AFS_GROUP];
  }
  if (!hasRepo) {
    delete assetGroups[REPO_GROUP];
  }
  if (!hasCDS) {
    delete assetGroups[CDS_GROUP];
  }
  // Show the standard Options allocation only for sessions that selected class 19.
  if (!hasSimplifiedOptions) {
    delete assetGroups[OPTIONS_GROUP];
  }

  const reset = () => {
    const newAllocations = {} as State;
    strategic_group_allocations.forEach(a => {
      newAllocations[a.asset_group] = {
        id: a.id,
        asset_group: a.asset_group,
        lower_limit: a.lower_limit,
        upper_limit: a.upper_limit,
      };
    });
    setAllocations(newAllocations);
    setErrors([]);
  };

  useEffect(reset, [strategic_group_allocations]);
  const dataSource = Object.entries(assetGroups).map(([key, name]) => {
    const assetGroup = parseInt(key);
    const setAllocation = strategic_group_allocations.find(
      a => a.asset_group === assetGroup
    );
    // console.log('---');
    // console.log(ASSETS_OF_GROUP[assetGroup]);
    const assetClassesInAssetGroup = ASSETS_OF_GROUP[assetGroup];
    const currentPercent = tactical_asset_allocations.reduce(
      (acc, allocation) => {
        if (assetClassesInAssetGroup.includes(allocation.asset_class)) {
          return acc + allocation.percent;
        }
        return acc;
      },
      0
    );

    return { name, assetGroup, setAllocation, currentPercent };
  });

  const renderCurrentPercent = () => {
    return {
      render(_: unknown, record: (typeof dataSource)[0]) {
        const playerSetAllocations = strategic_group_allocations.find(
          sga => sga.asset_group == record.assetGroup
        );
        if (playerSetAllocations === undefined) return;
        const currentPercent = record.currentPercent;
        if (
          currentPercent < playerSetAllocations.lower_limit ||
          currentPercent > playerSetAllocations.upper_limit
        ) {
          return (
            <div style={{ color: 'red' }}>
              {Math.round(record.currentPercent * 100)}%
            </div>
          );
        } else {
          return (
            <div style={{ color: 'green' }}>
              {Math.round(record.currentPercent * 100)}%
            </div>
          );
        }
      },
    };
  };

  const getProps = (field: 'lower_limit' | 'upper_limit') => {
    return {
      render(_: unknown, record: (typeof dataSource)[0]) {
        if (editing) {
          const assetGroup = record.assetGroup;

          const allocation = allocations[assetGroup];
          const limit = allocation?.[field] ?? 0;

          return (
            <div>
              <InputNumber
                size={'small'}
                value={limit}
                style={{ width: 70 }}
                onChange={value => {
                  if (value === undefined) {
                    return;
                  }
                  setAllocations({
                    ...allocations,
                    [assetGroup]: {
                      ...allocation,
                      asset_group: assetGroup,
                      [field]: value,
                    },
                  });
                }}
                step={0.01}
                min={0}
                max={1}
                formatter={value => {
                  const nr = Number(value) || 0;
                  return `${Math.round(nr * 100)}%`;
                }}
                parser={value => {
                  const parsedValue = `${value?.replaceAll('%', '')}`;
                  const nr = Number(parsedValue);
                  if (!isNaN(nr)) {
                    return Number((nr / 100).toFixed(2));
                  }
                  return 0;
                }}
              />
            </div>
          );
        }

        if (!record.setAllocation) {
          return <div>-</div>;
        }

        return <div>{Math.round(record.setAllocation[field] * 100)}%</div>;
      },
    };
  };

  const isValid = () => {
    const errorsList = [];

    const valuesAscending = Object.values(allocations).reduce(
      (acc, { lower_limit, upper_limit }) => {
        // Skip undefined cells
        if (!lower_limit && !upper_limit) {
          return acc;
        }
        return acc && (lower_limit ?? 0) < (upper_limit ?? 0);
      },
      true
    );

    if (!valuesAscending) {
      errorsList.push(
        'Lower limit values must be lesser than upper limit values!'
      );
    }

    if (errorsList.length) {
      setErrors(errorsList);
      return false;
    }

    return true;
  };

  const footer = () => {
    return (
      <div>
        {editButton}
        {checkClientProfile}
      </div>
    );
  };
  const checkClientProfile = (
    <div
      // style={{ position: 'relative', display: 'flex', gap: '10px', height: 30 }}
      style={{ display: 'inline-block' }}
    >
      <Button
        onClick={() => showClientProfileModal(true)}
        // style={{ position: 'absolute', left: 0 }}
      >
        Check client profile
      </Button>
    </div>
  );

  const editButton = (
    <div
      style={{ display: 'inline-block' }}

      // style={{ position: 'relative', display: 'flex', gap: '10px', height: 30 }}
    >
      {!editing && (
        <Button
          onClick={() => {
            setEditing(true);
          }}
          style={{ position: 'absolute', right: 15, bottom: 17 }}
        >
          Edit
        </Button>
      )}
      {editing && (
        <>
          <Button
            type="primary"
            onClick={() => {
              if (!isValid()) {
                return;
              }

              setEditing(false);
              dispatch(
                updatePlayerGroupAllocations({
                  allocations: Object.values(allocations),
                  player_id: player_id,
                })
              );
            }}
            style={{ position: 'absolute', right: 110, bottom: 17 }}
          >
            Save
          </Button>
          <Button
            onClick={() => {
              setEditing(false);
              reset();
            }}
            style={{ position: 'absolute', right: 15, bottom: 17 }}
          >
            Cancel
          </Button>
        </>
      )}
    </div>
  );
  const noData = (
    <Empty
      description={
        <Typography.Text type={'secondary'}>
          Select and save some instruments.
        </Typography.Text>
      }
    />
  );

  const noDataBehaviour = {
    emptyText: noData,
  };

  return (
    <div style={{ minWidth: 360 }}>
      {clientProfileModal}
      <Typography.Title level={4} style={{ display: 'inline-block' }}>
        Strategic Allocations
      </Typography.Title>
      <Tooltip
        styles={{ root: { maxWidth: '500px' } }}
        title={
          <div>
            {/*<div>*/}
            {/*  Setting target strategic allocations does not enforce hard limits.*/}
            {/*</div>*/}
            <br />
            <ul>
              <li>Equities: Equities, ETF Equities</li>
              <li>Bonds: Govt Bonds, Corporate Bonds, ETF Bonds</li>
              <li>Alternatives: Metals, ETF Commodities</li>
            </ul>
            {/*<div>*/}
            {/*  (red: over allocated, yellow: under allocated, green: within*/}
            {/*  guidelines)*/}
            {/*</div>*/}
          </div>
        }
      >
        <InfoCircleOutlined
          style={{
            display: 'inline-block',
            marginLeft: 10,
            // position: 'absolute',
            // marginLeft: 10,
            // top: 7,
          }}
        />
      </Tooltip>
      <Table
        rowKey="assetGroup"
        dataSource={dataSource}
        columns={[
          { title: 'Asset Class Group', dataIndex: 'name' },
          {
            title: 'Bank Mandate',
            children: [
              {
                title: 'Lower',
                ...getProps('lower_limit'),
              },
              { title: 'Upper', ...getProps('upper_limit') },
            ],
          },
          { title: 'Current', ...renderCurrentPercent() },
        ]}
        locale={noDataBehaviour}
        pagination={false}
        bordered
        footer={footer}
      />

      {editing && !!errors.length && (
        <div
          style={{
            marginTop: '10px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          {errors.map(e => {
            return (
              <div key={e} style={{ color: red[5] }}>
                {e}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PlayerStrategicAssetAllocations;
